import { differenceInCalendarDays, parseISO } from 'date-fns';
import { PARAMS } from '../../../constants/pages';
import { TIME_TO_HIRE_PERIOD_FILTER } from '../../../constants/timeToHire';
import { primaryPalette } from '../../../styles/theme';
import { truncateText } from '../../../utility/uiUtils';
import { getItemById } from '../../../utility/helpers';

export const getTimeToHirePeriodParams = period => {
  const selectedPeriod = TIME_TO_HIRE_PERIOD_FILTER.find(
    periodOption => periodOption.value === period
  )?.filter;

  return selectedPeriod
    ? {
        [PARAMS.DATE_FROM]: selectedPeriod.start,
        [PARAMS.DATE_TO]: selectedPeriod.end,
      }
    : {};
};

const CHART_BAR_COLOR = '#EAE9F0';
const HIRES_BAR_COLOR = '#E0C3FC';
const DAYS_BAR_COLOR = '#C0D6FF';
const CANDIDATES_BAR_COLOR = '#F3D1FF';
const DEFAULT_X_AXIS_MAX = 90;
const BAR_THICKNESS = 27;
const GROUPED_BAR_THICKNESS = 12;
const DEPARTMENT_LABEL_MAX_LENGTH = 22;
const JOB_CHART_LABEL_MAX_LENGTH = 14;

export const GROUPED_CHART_MAX_SELECTIONS = 4;
const GROUPED_CHART_SELECT_HEIGHT = 80;
const GROUPED_CHART_SELECTED_ITEM_HEIGHT = 44;

export const prepareGroupedChartControlsHeight = selectedCount =>
  GROUPED_CHART_SELECT_HEIGHT +
  selectedCount * GROUPED_CHART_SELECTED_ITEM_HEIGHT;
const DAYS_LABEL_OFFSET = 8;
const BAR_LABEL_LINE_HEIGHT = 18;
const BAR_LABEL_PADDING = 12;
const BAR_LABEL_MIN_GAP = 16;
const DEPARTMENT_LABEL_FONT = '14px ProximaNova-Bold';
const DAYS_LABEL_FONT = '14px ProximaNova-Regular';

const measureTextWidth = (ctx, text, font) => {
  ctx.font = font;

  return ctx.measureText(text).width;
};

const truncateTextToWidth = (ctx, text, maxWidth, font) => {
  ctx.font = font;

  if (measureTextWidth(ctx, text, font) <= maxWidth) {
    return text;
  }

  let truncated = text;

  while (
    truncated.length > 0 &&
    measureTextWidth(ctx, `${truncated}...`, font) > maxWidth
  ) {
    truncated = truncated.slice(0, -1);
  }

  return `${truncated.trimEnd()}...`;
};

const canFitLabelsInsideBar = (ctx, daysText, barWidth) => {
  const daysWidth = measureTextWidth(ctx, daysText, DAYS_LABEL_FONT);
  const availableNameWidth =
    barWidth - BAR_LABEL_PADDING * 2 - BAR_LABEL_MIN_GAP - daysWidth;

  return (
    availableNameWidth > measureTextWidth(ctx, '...', DEPARTMENT_LABEL_FONT)
  );
};

export const getTimeToHirePeriodMaxDays = period => {
  const selectedPeriod = TIME_TO_HIRE_PERIOD_FILTER.find(
    periodOption => periodOption.value === period
  )?.filter;

  if (!selectedPeriod) {
    return null;
  }

  return differenceInCalendarDays(
    parseISO(selectedPeriod.end),
    parseISO(selectedPeriod.start)
  );
};

const getDepartmentChartStepSize = maxDays => {
  if (maxDays <= 30) {
    return 5;
  }

  if (maxDays <= 90) {
    return 15;
  }

  if (maxDays <= 180) {
    return 30;
  }

  return 60;
};

const getDepartmentChartXMax = (period, data) => {
  const periodMaxDays = getTimeToHirePeriodMaxDays(period);

  if (periodMaxDays) {
    return periodMaxDays;
  }

  const dataMaxDays = Math.max(...data.map(item => item.days), 0);

  if (dataMaxDays === 0) {
    return DEFAULT_X_AXIS_MAX;
  }

  const stepSize = getDepartmentChartStepSize(dataMaxDays);

  return Math.ceil(dataMaxDays / stepSize) * stepSize;
};

export const filterDepartmentDataByPeriod = (data, period) => {
  const periodMaxDays = getTimeToHirePeriodMaxDays(period);

  return periodMaxDays ? data.filter(item => item.days <= periodMaxDays) : data;
};

export const getTimeToHirePeriodOptions = labels =>
  TIME_TO_HIRE_PERIOD_FILTER.map(period => ({
    ...period,
    label: labels[period.key],
  }));

export const buildDepartmentChartData = (data, period) => {
  const filteredData = filterDepartmentDataByPeriod(data, period);

  return {
    labels: filteredData.map(item => item.name),
    datasets: [
      {
        data: filteredData.map(item => item.days),
        backgroundColor: CHART_BAR_COLOR,
        borderSkipped: false,
        barThickness: BAR_THICKNESS,
      },
    ],
  };
};

export const prepareDepartmentChartOptions = (
  translations,
  period,
  data = []
) => {
  const xMax = getDepartmentChartXMax(period, data);
  const stepSize = getDepartmentChartStepSize(xMax);

  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        right: 16,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          title: items => {
            const { dataIndex, chart } = items[0];

            return chart.data.labels[dataIndex];
          },
          label: item => `${item.raw} ${translations.daysSuffix}`,
        },
        backgroundColor: primaryPalette.white,
        titleColor: primaryPalette.bluish1,
        bodyColor: primaryPalette.bluish1,
        padding: 10,
        cornerRadius: 8,
        borderWidth: 1,
        borderColor: primaryPalette.bluish7,
      },
    },
    scales: {
      x: {
        min: 0,
        max: xMax,
        border: {
          display: true,
          color: primaryPalette.bluish4,
        },
        grid: {
          color: primaryPalette.bluish7,
          borderDash: [4, 4],
          drawTicks: false,
        },
        ticks: {
          stepSize,
          align: 'inner',
          color: primaryPalette.bluish1,
          font: {
            size: 14,
            family: 'ProximaNova-Regular',
          },
          padding: 8,
          callback: value => `${value} ${translations.daysSuffix}`,
        },
      },
      y: {
        border: {
          display: true,
          color: primaryPalette.bluish4,
        },
        grid: {
          display: false,
          drawTicks: false,
        },
        ticks: {
          display: false,
        },
      },
    },
  };
};

export const getGroupedBarChartItemId = item => item.id; // ?? item.name;

export const getGroupedBarChartSelectOptions = (data = []) =>
  data.map(item => {
    const optionId = getGroupedBarChartItemId(item);

    return {
      id: optionId,
      value: optionId,
      label: item.name,
      name: item.name,
    };
  });

export const getInitialGroupedChartSelection = (data = []) =>
  data.slice(0, GROUPED_CHART_MAX_SELECTIONS).map(getGroupedBarChartItemId);

export const getOrderedGroupedChartData = (data = [], selectedIds = []) =>
  selectedIds.map(id => getItemById(data, id)).filter(Boolean);

export const formatRecruiterChartLabel = name => {
  if (!name?.trim()) {
    return '';
  }

  const parts = name.trim().split(/\s+/);

  if (parts.length === 1) {
    return parts[0];
  }

  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');

  return `${firstName.charAt(0).toUpperCase()}. ${lastName}`;
};

export const formatJobChartLabel = name =>
  truncateText(name ?? '', JOB_CHART_LABEL_MAX_LENGTH);

const getGroupedChartFullLabels = data => data.map(item => item.name);

export const buildGroupedBarChartData = (
  translations,
  data = [],
  formatLabel = name => name
) => {
  const fullLabels = getGroupedChartFullLabels(data);

  return {
    labels: data.map(item => formatLabel(item.name)),
    fullLabels,
    datasets: [
      {
        label: translations.hiresLegendLabel,
        data: data.map(item => item.hires),
        backgroundColor: HIRES_BAR_COLOR,
        borderSkipped: false,
        barThickness: GROUPED_BAR_THICKNESS,
        fullLabels,
      },
      {
        label: translations.daysLegendLabel,
        data: data.map(item => item.days),
        backgroundColor: DAYS_BAR_COLOR,
        borderSkipped: false,
        barThickness: GROUPED_BAR_THICKNESS,
      },
      {
        label: translations.candidatesLegendLabel,
        data: data.map(item => item.candidates),
        backgroundColor: CANDIDATES_BAR_COLOR,
        borderSkipped: false,
        barThickness: GROUPED_BAR_THICKNESS,
      },
    ],
  };
};

const getGroupedChartTooltipTitle = items => {
  const { dataIndex, chart } = items[0];
  const fullLabels = chart.data.datasets[0]?.fullLabels;

  return fullLabels?.[dataIndex] ?? '';
};

const getGroupedBarYMax = data => {
  const maxValue = Math.max(
    ...data.flatMap(item => [item.hires, item.days]),
    0
  );

  if (maxValue === 0) {
    return 10;
  }

  return Math.ceil(maxValue / 10) * 10;
};

export const prepareGroupedBarChartOptions = (data = []) => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 24,
    },
  },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        pointStyle: 'rectRounded',
        color: primaryPalette.bluish1,
        boxWidth: 10,
        boxHeight: 10,
        padding: 24,
        font: {
          size: 14,
          family: 'ProximaNova-Regular',
        },
      },
    },
    tooltip: {
      displayColors: true,
      callbacks: {
        title: getGroupedChartTooltipTitle,
      },
      backgroundColor: primaryPalette.white,
      titleColor: primaryPalette.bluish1,
      bodyColor: primaryPalette.bluish1,
      padding: 10,
      cornerRadius: 8,
      borderWidth: 1,
      borderColor: primaryPalette.bluish7,
    },
  },
  scales: {
    x: {
      border: {
        display: true,
        color: primaryPalette.bluish6,
      },
      grid: {
        display: false,
        drawTicks: false,
      },
      ticks: {
        color: primaryPalette.bluish1,
        font: {
          size: 14,
          family: 'ProximaNova-Regular',
        },
        padding: 8,
      },
    },
    y: {
      max: getGroupedBarYMax(data),
      beginAtZero: true,
      border: {
        display: true,
        color: primaryPalette.bluish6,
      },
      grid: {
        color: primaryPalette.bluish7,
        borderDash: [4, 4],
        drawTicks: false,
      },
      ticks: {
        display: false,
      },
    },
  },
});

export const getGroupedBarLabelsPlugin = () => ({
  id: 'groupedBarLabels',
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart;

    ctx.save();
    ctx.fillStyle = primaryPalette.bluish1;
    ctx.font = '14px ProximaNova-Regular';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];

        ctx.fillText(value, bar.x, bar.y - 6);
      });
    });

    ctx.restore();
  },
});

const drawDepartmentLabelsInsideBar = (
  ctx,
  departmentName,
  daysText,
  barLeft,
  barRight,
  barY,
  barWidth
) => {
  const daysWidth = measureTextWidth(ctx, daysText, DAYS_LABEL_FONT);
  const availableNameWidth =
    barWidth - BAR_LABEL_PADDING * 2 - BAR_LABEL_MIN_GAP - daysWidth;
  const displayDepartmentName = truncateTextToWidth(
    ctx,
    departmentName,
    availableNameWidth,
    DEPARTMENT_LABEL_FONT
  );

  ctx.fillStyle = primaryPalette.bluish1;

  ctx.font = DEPARTMENT_LABEL_FONT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(displayDepartmentName, barLeft + BAR_LABEL_PADDING, barY);

  ctx.font = DAYS_LABEL_FONT;
  ctx.textAlign = 'right';
  ctx.fillText(daysText, barRight - BAR_LABEL_PADDING, barY);
};

const drawDepartmentLabelsAboveBar = (
  ctx,
  departmentName,
  daysText,
  barRight,
  barTop
) => {
  const labelX = barRight + DAYS_LABEL_OFFSET;

  ctx.fillStyle = primaryPalette.bluish1;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'bottom';

  ctx.font = DEPARTMENT_LABEL_FONT;
  ctx.fillText(
    truncateText(departmentName, DEPARTMENT_LABEL_MAX_LENGTH),
    labelX,
    barTop - BAR_LABEL_LINE_HEIGHT + 28
  );

  ctx.font = DAYS_LABEL_FONT;
  ctx.fillText(daysText, labelX, barTop + 28);
};

export const getDepartmentBarLabelsPlugin = translations => ({
  id: 'departmentBarLabels',
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart;
    const meta = chart.getDatasetMeta(0);

    if (!meta?.data?.length) {
      return;
    }

    ctx.save();

    meta.data.forEach((bar, index) => {
      const departmentName = data.labels[index];
      const value = data.datasets[0].data[index];
      const daysText = `${value} ${translations.daysSuffix}`;
      const barLeft = Math.min(bar.x, bar.base);
      const barRight = Math.max(bar.x, bar.base);
      const barWidth = barRight - barLeft;
      const barTop = bar.y - bar.height / 2;

      if (canFitLabelsInsideBar(ctx, daysText, barWidth)) {
        drawDepartmentLabelsInsideBar(
          ctx,
          departmentName,
          daysText,
          barLeft,
          barRight,
          bar.y,
          barWidth
        );
      } else {
        drawDepartmentLabelsAboveBar(
          ctx,
          departmentName,
          daysText,
          barRight,
          barTop
        );
      }
    });

    ctx.restore();
  },
});
